import { useCallback, useEffect, useState } from "react";

import { create } from "@bufbuild/protobuf";

import { GetPresignedDownloadUriRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";
import { SourceResource } from "@gx/ts-contracts/services/source/resources/source_pb";

import { useGetPresignedDownloadUriQuery } from "@/api/queries/sources";

interface UseEditSourceTextResult {
  textContent: string;
  isLoading: boolean;
  error: string | null;
}

export function useEditSourceText(source: SourceResource): UseEditSourceTextResult {
  const [result, setResult] = useState<UseEditSourceTextResult>({
    textContent: "",
    isLoading: true,
    error: null,
  });

  const config = source.configuration?.configuration;
  if (!config || config.case !== "sourceText") {
    throw new Error("Invalid source configuration: expected sourceText");
  }

  const file = config.value.files[0];
  if (!file) {
    throw new Error("Invalid source configuration: no file found");
  }

  const objectStorageUri = file.objectStorageUri;
  if (!objectStorageUri) {
    throw new Error("Invalid source configuration: file has no object storage URI");
  }

  const { data: presignedData, isLoading: isPresignedLoading } = useGetPresignedDownloadUriQuery({
    input: create(GetPresignedDownloadUriRequestSchema, {
      objectStorageUri,
    }),
  });

  const fetchContent = useCallback(async (downloadUri: string) => {
    try {
      setResult((prev) => ({ ...prev, isLoading: true, error: null }));

      const response = await fetch(downloadUri);
      if (!response.ok) {
        throw new Error(`Failed to fetch content: ${response.status} ${response.statusText}`);
      }

      const content = await response.text();
      setResult({ textContent: content, isLoading: false, error: null });
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : "Failed to load content";
      setResult((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
    }
  }, []);

  useEffect(() => {
    if (!result.textContent && presignedData?.downloadUri) {
      void fetchContent(presignedData.downloadUri);
    }
  }, [result.textContent, presignedData?.downloadUri, fetchContent]);

  return { ...result, isLoading: result.isLoading || isPresignedLoading };
}
